/**
 * ChatAvatar — WhatsApp-style circular chat avatar (V1.9.30).
 *
 * Given a name (or image URL), renders a filled colored circle with an
 * initial letter in white bold. If `src` is provided, shows the image
 * instead. Color is deterministic from the name (hashed → HSL) so the
 * same user always gets the same swatch.
 *
 * Props:
 *   name    : string used for initial + color hash
 *   src     : optional image URL
 *   size    : "sm" (36 px) | "md" (48 px, default) | "lg" (56 px)
 *   isGroup : renders a group icon instead of an initial
 *   testId  : optional data-testid
 */
import React from "react";
import { UsersThree } from "@phosphor-icons/react";

const SIZE_CLASS = {
    sm: { box: "w-9 h-9", text: "text-sm", icon: 18 },
    md: { box: "w-12 h-12", text: "text-base", icon: 22 },
    lg: { box: "w-14 h-14", text: "text-lg", icon: 26 },
};

function hashHue(str) {
    let h = 0;
    for (let i = 0; i < str.length; i += 1) {
        h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
    }
    return Math.abs(h) % 360;
}

export default function ChatAvatar({ name = "?", src, size = "md", isGroup = false, testId }) {
    const s = SIZE_CLASS[size] || SIZE_CLASS.md;
    const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
    const hue = hashHue(name || "team");
    const bg = `hsl(${hue}, 42%, 55%)`;
    // If src is a relative /api/... path, prepend the backend URL.
    let finalSrc = src;
    if (src && src.startsWith("/api/")) {
        finalSrc = `${process.env.REACT_APP_BACKEND_URL}${src}`;
    }
    if (finalSrc) {
        return (
            <img
                src={finalSrc}
                alt={name}
                data-testid={testId}
                className={`${s.box} rounded-full object-cover object-center shrink-0 ring-1 ring-black/5`}
            />
        );
    }
    if (isGroup) {
        return (
            <span
                data-testid={testId}
                className={`${s.box} rounded-full flex items-center justify-center shrink-0 text-white ring-1 ring-black/5`}
                style={{ backgroundColor: "hsl(200, 20%, 80%)" }}
            >
                <UsersThree size={s.icon} weight="fill" className="text-ink/70" />
            </span>
        );
    }
    return (
        <span
            data-testid={testId}
            className={`${s.box} rounded-full flex items-center justify-center shrink-0 text-white font-bold ${s.text} ring-1 ring-black/5`}
            style={{ backgroundColor: bg }}
        >
            {initial}
        </span>
    );
}
